/**
 * Platform tour: Dashboard -> Appointments -> Video Call -> Patients
 * -> Admin Intelligence -> Settings -> Admin Settings.
 */
export const PLATFORM_TOUR_STEPS = [
  { path: "/", label: "Dashboard", section: "Daily Operations" },
  { 
    path: "/appointments", 
    label: "Appointments", 
    section: "Daily Operations",
    permissionChecks: [
      { required: "appointments.select_providers", level: "read" },
      { required: "appointments.add", level: "write" },
      { required: "appointments.modify", level: "write" },
      { required: "appointments.delete", level: "write" },
      { required: "appointments.patient_reports", level: "read" },
      { required: "appointments.join_call", level: "write" },
      { required: "appointments.post_call_doc", level: "read" },
    ]
  },
  { 
    path: "/video-call", 
    label: "Video Call", 
    section: "Daily Operations",
    permissionChecks: [
      { required: "video_call.upcoming", level: "read" },
      { required: "video_call.history", level: "read" },
      { required: "video_call.add", level: "read" },
    ]
  },
  { 
    path: "/patients", 
    label: "Patients", 
    section: "Patients", 
    unifiedFeatures: true,
    permissionChecks: [
      { required: "patients.info", level: "read" },
      { required: "patients.clinical_summary", level: "read" },
      { required: "patients.upcoming_appointment", level: "write" },
      { required: "patients.join_call", level: "write" },
      { required: "patients.previous_calls", level: "read" },
      { required: "patients.post_call_doc", level: "read" },
    ]
  },
  { 
    path: "/reports", 
    label: "Admin Intelligence Hub", 
    section: "Admin Intelligence",
    permissionChecks: [
      { required: "reports.billing_analytics", level: "read" },
      { required: "reports.billing_history", level: "read" },
      { required: "reports.estimated_billing", level: "read" },
    ]
  },
  { 
    path: "/reports/billing-analytics", 
    label: "Billing Analytics", 
    section: "Admin Intelligence",
    permissionChecks: [{ required: "reports.billing_analytics", level: "read" }]
  },
  { 
    path: "/reports/billing-history", 
    label: "Billing History", 
    section: "Admin Intelligence",
    permissionChecks: [{ required: "reports.billing_history", level: "read" }]
  },
  { 
    path: "/bill-calculation", 
    label: "Estimated Billing", 
    section: "Admin Intelligence",
    permissionChecks: [{ required: "reports.estimated_billing", level: "read" }]
  },
  { path: "/settings", label: "Settings Hub", section: "Settings" },
  { path: "/settings/profile", label: "Profile & Settings", section: "Settings" },
  { 
    path: "/settings/ehr-integration", 
    label: "EHR Integration", 
    section: "Settings",
    permissionChecks: [{ required: "settings.ehr_integration", level: "read" }]
  },
  { 
    path: "/settings/subscription", 
    label: "Subscription & Plans", 
    section: "Settings",
    permissionChecks: [{ required: "settings.payment_billing", level: "read" }]
  },
  { 
    path: "/settings/payment-billing", 
    label: "Payment & Billing", 
    section: "Settings",
    permissionChecks: [{ required: "settings.payment_billing", level: "read" }]
  },
  { 
    path: "/admin/rbac", 
    label: "Admin Settings", 
    section: "Admin Settings",
    permissionChecks: [{ required: "admin.manage_rbac", level: "read" }]
  },
];

export const pagesConfig = {
  "/": {
    title: "Dashboard",
    description:
      "Review daily schedules, appointment health, and workforce intelligence from one command center.",
    features: [
      {
        title: "Welcome Header",
        description:
          "See the signed-in clinician, clinic context, and quick paths into appointments or calls.",
        selector: "[data-tour='welcome-card']",
      },
      {
        title: "Practice Intelligence",
        description:
          "Track total scheduled visits, in-person appointments, and virtual appointments for today.",
        selector: "[data-tour='appointment-stats']",
      },
      {
        title: "Status Overview",
        description:
          "Review patient risk and visit status signals without leaving the dashboard.",
        selector: "[data-tour='appointment-status']",
      },
      {
        title: "Workforce Intelligence",
        description:
          "Compare provider coverage and workforce intelligence for the selected day.",
        selector: "[data-tour='provider-workload']",
      },
      {
        title: "AI Chatbot Assistant",
        description:
          "Quickly access the AI chatbot to assist with patient context, billing codes, or daily queries directly from any page.",
        selector: "[data-chatbot-trigger]",
      },
    ],
    nextActions: ["Review today's schedule", "Open appointments"],
  },

  "/appointments": {
    title: "Appointments",
    description:
      "Coordinate calendar scheduling, provider filters, and appointment creation.",
    features: [
      {
        title: "Appointments Navigation",
        description:
          "Use the page header to keep context while managing appointment schedules.",
        selector: "[data-tour='calendar-header']",
      },
      {
        title: "Calendar Controls",
        description:
          "Move through dates, refresh appointments, and manage the active calendar range.",
        selector: "[data-tour='calendar-controls-main']",
      },
      {
        title: "View Switcher",
        description:
          "Switch between day, week, month, and agenda views for the schedule.",
        selector: "[data-tour='calendar-view-selector']",
      },
      {
        title: "Clinician Filter",
        description:
          "Filter appointments by one or more clinicians when your role allows it.",
        selector: "[data-tour='calendar-filter']",
      },
      {
        title: "Add Appointment",
        description:
          "Create a single appointment or start a bulk upload from the add menu.",
        selector: "[data-tour='calendar-add-btn']",
      },
      {
        title: "Calendar Grid",
        description:
          "Review, select, and manage scheduled appointments on the interactive calendar.",
        selector:
          "[data-tour='calendar-grid'] .rbc-event, [data-tour='calendar-grid'] .rbc-day-slot, [data-tour='calendar-grid'] .rbc-month-view, [data-tour='calendar-grid'] .rbc-agenda-view",
      },
    ],
    nextActions: ["Change the calendar view", "Add an appointment"],
  },

  "/video-call": {
    title: "Video Call",
    description:
      "Manage upcoming virtual visits, join sessions, and review video-call history.",
    features: [
      {
        title: "Video Consultation",
        description:
          "Use the page header to confirm you are in the secure video-call workspace.",
        selector: "[data-tour='videocall-header']",
        prepare: "video-tab-upcoming",
      },
      {
        title: "Add Appointment Slot",
        description:
          "Create a new video appointment slot when your role has access.",
        selector: "[data-tour='videocall-add-btn']",
        prepare: "video-tab-upcoming",
      },
      {
        title: "Consultation Tabs",
        description:
          "Switch between upcoming calls, joining by ID, and call history.",
        selector: "[data-tour='videocall-tabs']",
      },
      {
        title: "Upcoming Calls",
        description:
          "Open the upcoming calls tab to manage scheduled video visits.",
        selector: "[data-tour='tab-upcoming']",
        prepare: "video-tab-upcoming",
      },
      {
        title: "Select Appointment",
        description:
          "Choose an upcoming appointment to load room details and sharing options.",
        selector: "[data-tour='videocall-select']",
        prepare: "video-select-online-appointment",
      },
      {
        title: "Appointment Type Selector",
        description:
          "Switch the selected appointment to online so meeting-room controls are available.",
        selector: "[data-tour='videocall-type']",
        prepare: "video-select-online-appointment",
      },
      {
        title: "Appointment Details",
        description:
          "Review patient, appointment ID, date, time, and visit reason before the call.",
        selector: "[data-tour='videocall-details']",
        prepare: "video-select-online-appointment",
      },
      {
        title: "Identity & Room Settings",
        description:
          "Confirm your display name and generated meeting ID before starting.",
        selector: "[data-tour='videocall-inputs']",
        prepare: "video-select-online-appointment",
      },
      {
        title: "Start Video Call",
        description:
          "Start the secure video call when the appointment is ready.",
        selector: "[data-tour='videocall-actions']",
        prepare: "video-select-online-appointment",
      },
      {
        title: "Copy Shareable Link",
        description:
          "Copy the patient invite link after an online appointment is selected.",
        selector: "[data-tour='videocall-share']",
        prepare: "video-select-online-appointment",
      },
      {
        title: "Join by ID",
        description:
          "Switch to the join tab to enter an active meeting room manually.",
        selector: "[data-tour='tab-join']",
        prepare: "video-tab-join",
      },
      {
        title: "Join Session Details",
        description:
          "Enter a display name and meeting ID to join an active call.",
        selector: "[data-tour='videocall-join-form']",
        prepare: "video-tab-join",
      },
      {
        title: "Call History Logs",
        description:
          "Switch to call history to review completed video sessions.",
        selector: "[data-tour='tab-history']",
        prepare: "video-tab-history",
      },
      {
        title: "Log Filter Options",
        description:
          "Filter past calls by provider, date range, or patient name.",
        selector: "[data-tour='history-filters']",
        prepare: "video-tab-history",
      },
      {
        title: "Past Consultation List",
        description:
          "Browse completed calls with patient, provider, date, and time context.",
        selector: "[data-tour='history-list']",
        prepare: "video-tab-history",
      },
      {
        title: "SOAP & Documentation",
        description:
          "Open Practice Intelligence from a completed session when available.",
        selector: "[data-tour='history-soap-btn']",
        prepare: "video-tab-history",
      },
    ],
    nextActions: ["Select an upcoming video visit", "Review call history"],
  },

  "/patients": {
    title: "Patients",
    description:
      "Access and manage patient records securely—search, filter, and open profiles for visit history.",
    features: [], // populated via PATIENTS_LIST_FEATURES in enrichPatientsPageConfig
    nextActions: ["Search for a patient", "Open Patient Reports with the eye icon"],
  },

  "/reports": {
    title: "Reports",
    description:
      "Open each billing area from the hub: analytics, history, and estimated billing.",
    features: [
      {
        title: "Billing Analytics",
        description:
          "Review revenue trends, KPIs, and CPT frequency for your practice.",
        selector: "[data-tour='reports-analytics-card']",
      },
      {
        title: "Billing History",
        description: "View invoice history and payment statuses.",
        selector: "[data-tour='reports-history-card']",
      },
      {
        title: "Estimated Billing",
        description: "Access estimated billing and calculation tools.",
        selector: "[data-tour='reports-estimated-card']",
      },
    ],
    nextActions: ["Open billing analytics", "Review billing history"],
  },

  "/reports/billing-analytics": {
    title: "Admin Intelligence",
    description:
      "Review billing activity, revenue KPIs, session details, and CPT code distribution.",
    features: [
      {
        title: "Period Filters",
        description:
          "Switch between week, month, quarter, year, or a custom date range.",
        selector: "[data-tour='billing-period-filters']",
      },
      {
        title: "Revenue KPIs",
        description:
          "Track total revenue, active providers, top specialty, and average session length.",
        selector: "[data-tour='billing-kpis']",
      },
      {
        title: "Billing Details",
        description: "Drill into doctor sessions and open line-item details.",
        selector: "[data-tour='billing-details-table']",
      },
      {
        title: "CPT Code Frequency",
        description: "See which procedure codes appear most often in billing.",
        selector: "[data-tour='billing-cpt-chart']",
      },
    ],
    nextActions: ["Change the reporting period", "Open billing history"],
  },

  "/reports/billing-history": {
    title: "Billing History",
    description: "Search and review invoices with payment status at a glance.",
    features: [
      {
        title: "Search & Filters",
        description: "Find invoices by ID, patient, doctor, or payment status.",
        selector: "[data-tour='billing-history-filters']",
      },
      {
        title: "Summary Cards",
        description: "Quick totals for paid, pending, and declined invoices.",
        selector: "[data-tour='billing-history-summary']",
      },
      {
        title: "Seat-based Cost Breakdown",
        description:
          "See how base plan and additional seats contribute to monthly spend.",
        selector: "[data-tour='billing-history-seat-breakdown']",
      },
      {
        title: "Invoice Table",
        description: "Review line items, CPT codes, amounts, and download options.",
        selector: "[data-tour='billing-history-table']",
      },
    ],
    nextActions: ["Filter by status", "Export invoice data"],
  },

  "/bill-calculation": {
    title: "Estimated Billing",
    description: "Estimate charges before finalizing claims and invoices.",
    features: [
      {
        title: "Billing Calculator",
        description:
          "Enter provider details, duration, and CPT codes to calculate a rough estimate.",
        selector: "[data-tour='bill-calculation-main']",
      },
    ],
    nextActions: ["Run an estimate", "Return to Reports hub"],
  },

  "/settings": {
    title: "Settings",
    description:
      "Manage profile, EHR integration, subscription, payment, and admin access from one hub.",
    features: [
      {
        title: "Profile & Settings",
        description:
          "Update personal, professional, and clinic information plus legal acknowledgements.",
        selector: "[data-tour='settings-profile-card']",
      },
      {
        title: "EHR Integration",
        description: "Connect Athena and manage EHR sync for clinical workflows.",
        selector: "[data-tour='settings-ehr-card']",
      },
      {
        title: "Subscription & Plans",
        description: "Manage your plan, seats, and add-ons.",
        selector: "[data-tour='settings-subscription-card']",
      },
      {
        title: "Payment & Billing",
        description: "Review payment methods and complete checkout.",
        selector: "[data-tour='settings-payment-card']",
      },
      {
        title: "Payment Methods",
        description: "Manage saved cards and linked Venmo accounts.",
        selector: "[data-tour='settings-payment-methods-card']",
      },
      {
        title: "Admin Settings",
        description: "Open role and permission management for your clinic.",
        selector: "[data-tour='settings-admin-card']",
      },
    ],
    nextActions: ["Open profile settings", "Open admin settings"],
  },

  "/settings/profile": {
    title: "Profile & Settings",
    description:
      "Keep your profile accurate for scheduling, billing, and compliance.",
    features: [
      {
        title: "My Profile",
        description: "Switch between profile fields and legal acknowledgements.",
        selector: "[data-tour='settings-tab-profile']",
        prepare: "profile-tab-profile",
      },
      {
        title: "Basic Profile",
        description: "Name, role, and contact emails used across the platform.",
        selector: "[data-tour='profile-basic']",
        prepare: "profile-tab-profile",
      },
      {
        title: "Professional Information",
        description: "NPI, specialty, license, and transcript retention settings.",
        selector: "[data-tour='profile-professional']",
        prepare: "profile-tab-profile",
      },
      {
        title: "Clinic Information",
        description: "Clinic name, address, and account identifiers.",
        selector: "[data-tour='profile-clinic']",
        prepare: "profile-tab-profile",
      },
      {
        title: "Security & Account",
        description: "Review authentication and session protection details.",
        selector: "[data-tour='profile-security']",
        prepare: "profile-tab-profile",
      },
      {
        title: "Legal Acknowledgements",
        description: "Open the legal tab for terms, privacy, and clinical agreements.",
        selector: "[data-tour='settings-tab-legal']",
        prepare: "profile-tab-legal",
      },
      {
        title: "Agreements",
        description:
          "Review and acknowledge terms, privacy, AI usage, and clinical responsibility.",
        selector: "[data-tour='profile-legal-content']",
        prepare: "profile-tab-legal",
      },
    ],
    nextActions: ["Update profile fields", "Review legal acknowledgements"],
  },

  "/settings/ehr-integration": {
    title: "EHR Integration",
    description: "Manage Athena connectivity for clinical data exchange.",
    features: [
      {
        title: "Connection Status",
        description: "See whether Athena is connected and last sync health.",
        selector: "[data-tour='ehr-status']",
      },
      {
        title: "Integration Actions",
        description: "Configure credentials and trigger sync from this panel.",
        selector: "[data-tour='ehr-actions']",
      },
    ],
    nextActions: ["Connect to Athena", "Return to Settings"],
  },

  "/settings/subscription": {
    title: "Subscription & Plans",
    description: "View your plan, seats, and billing cycle.",
    features: [
      {
        title: "Plans & Seats",
        description: "Compare plans and adjust seat count for your clinic.",
        selector: "[data-tour='subscription-plans']",
      },
      {
        title: "Usage Summary",
        description: "Monitor seat usage and renewal timing.",
        selector: "[data-tour='subscription-usage']",
      },
      {
        title: "Supporting Users",
        description:
          "Invite nurse practitioners or back-office staff and manage active seats.",
        selector: "[data-tour='subscription-seats']",
      },
    ],
    nextActions: ["Choose a plan", "Proceed to checkout"],
  },

  "/settings/payment-methods": {
    title: "Payment Methods",
    description: "Manage saved cards and linked payment accounts.",
    features: [
      {
        title: "Saved Cards",
        description: "Add, remove, or set a default credit or debit card.",
        selector: "[data-tour='payment-methods-cards']",
      },
      {
        title: "Venmo Business",
        description: "Link or unlink your Venmo Business profile for billing.",
        selector: "[data-tour='payment-methods-venmo']",
      },
    ],
    nextActions: ["Add a card", "Return to Settings"],
  },

  "/settings/payment-billing": {
    title: "Payment & Billing",
    description: "Manage payment methods and billing preferences.",
    features: [
      {
        title: "Payment Methods",
        description: "Add or update cards and billing contact details.",
        selector: "[data-tour='payment-methods']",
      },
      {
        title: "Checkout",
        description: "Complete purchases and review recent payment activity.",
        selector: "[data-tour='payment-checkout']",
      },
    ],
    nextActions: ["Select a payment method", "Complete payment"],
  },

  "/admin/rbac": {
    title: "Admin Settings",
    description:
      "Manage user permissions, roles, pending approvals, and team invitations.",
    features: [
      {
        title: "User Permissions Tab",
        description: "Assign and override permissions for selected users.",
        selector: "[data-tour='admin-tab-permissions']",
        prepare: "admin-tab-permissions",
      },
      {
        title: "Select Users",
        description: "Search and select users to review or change access.",
        selector: "[data-tour='admin-users-list']",
        prepare: "admin-tab-permissions",
      },
      {
        title: "Permission Sections",
        description:
          "Expand modules and set read/write access for selected users.",
        selector: "[data-tour='admin-permissions-panel']",
        prepare: "admin-tab-permissions",
      },
      {
        title: "Role Management",
        description: "Switch to create and edit clinic roles.",
        selector: "[data-tour='admin-tab-roles']",
        prepare: "admin-tab-roles",
      },
      {
        title: "Clinic Roles",
        description: "View system and custom roles; create new roles as needed.",
        selector: "[data-tour='admin-roles-grid']",
        prepare: "admin-tab-roles",
      },
      {
        title: "Role Editor",
        description: "Define role name and module permissions in the editor.",
        selector: "[data-tour='admin-role-editor']",
        prepare: "admin-tab-roles",
      },
      {
        title: "Pending Approvals",
        description: "Review users awaiting clinic approval.",
        selector: "[data-tour='admin-tab-approvals']",
        prepare: "admin-tab-approvals",
      },
      {
        title: "Approval Queue",
        description: "Approve or reject pending users from this list.",
        selector: "[data-tour='admin-approvals-list']",
        prepare: "admin-tab-approvals",
      },
      {
        title: "Invite Users",
        description: "Send invitations for new team members.",
        selector: "[data-tour='admin-tab-invites']",
        prepare: "admin-tab-invites",
      },
      {
        title: "Invite Form",
        description: "Enter email and role, then send an invitation.",
        selector: "[data-tour='admin-invite-form']",
        prepare: "admin-tab-invites",
      },
      {
        title: "Pending Invitations",
        description: "Track outstanding invites and revoke when needed.",
        selector: "[data-tour='admin-invites-pending']",
        prepare: "admin-tab-invites",
      },
    ],
    nextActions: ["Review permissions", "Invite a team member"],
  },

  fallback: {
    title: "Current Page",
    description:
      "The guided tour follows this page. Navigate using the sidebar while the assistant highlights available sections.",
    features: [
      {
        title: "Page controls",
        description:
          "Links, buttons, filters, and forms stay interactive while the tour is active.",
        selector: "main button, main a, main input, main select",
      },
    ],
    nextActions: ["Keep exploring", "Use Start Tour in the header anytime"],
  },
};

export const PATIENTS_LIST_FEATURES = [
  {
    title: "Patient Search",
    description:
      "Search by name or open Advanced Search for date of birth, phone, and email.",
    selector: "[data-tour='patients-search']",
    prepare: "open-patients-advanced",
  },
  {
    title: "Add Patient",
    description: "Create a new patient record and schedule when needed.",
    selector: "[data-tour='patients-add']",
  },
  {
    title: "Appointment Filters",
    description:
      "Filter the patient list by doctor and appointment date range.",
    selector: "[data-tour='patients-filters']",
  },
  {
    title: "Patient List",
    description:
      "Browse patients with MRN, insurance, visit dates, and assigned doctor.",
    selector: "[data-tour='patients-table']",
  },
  {
    title: "View Patient Records",
    description:
      "Use the eye icon to open Patient Reports for this patient's full record.",
    selector: "[data-tour='patients-view-record']",
  },
];

export const PATIENTS_DETAIL_FEATURES = [
  {
    title: "Patient Overview",
    description:
      "Review demographics, clinic, insurance, contact details, and the longitudinal clinical summary together.",
    selector: "[data-tour='patient-overview']",
    prepare: "expand-patient-clinical-summary",
  },
  {
    title: "Upcoming Appointment",
    description: "See the next scheduled visit date, time, and status.",
    selector: "[data-tour='patient-upcoming']",
  },
  {
    title: "Join Call",
    description:
      "Start a video visit from the upcoming appointment when the session is available.",
    selector: "[data-tour='patient-join-call']",
  },
  {
    title: "Appointment History",
    description:
      "Browse past and current appointments with doctor, status, and duration.",
    selector: "[data-tour='patient-appointment-history']",
  },
  {
    title: "Visit Documentation",
    description:
      "Open Practice Intelligence for a completed visit using the eye icon.",
    selector: "[data-tour='patient-visit-documentation']",
  },
];

const dynamicMatchers = [
  {
    pattern: /^\/patients\/[^/]+$/,
    config: {
      title: "Patients",
      description:
        "Review patient information, clinical summary, upcoming visits, and visit history.",
      features: [], // populated via PATIENTS_DETAIL_FEATURES in enrichPatientsPageConfig
      nextActions: ["Review appointment history", "Return to Patients list"],
    },
  },
  { pattern: /^\/billing-reports$/, config: pagesConfig["/reports/billing-analytics"] },
  { pattern: /^\/billing-history$/, config: pagesConfig["/reports/billing-history"] },
  { pattern: /^\/profile-settings$/, config: pagesConfig["/settings/profile"] },
  { pattern: /^\/athena-integration$/, config: pagesConfig["/settings/ehr-integration"] },
  { pattern: /^\/subscription$/, config: pagesConfig["/settings/subscription"] },
  { pattern: /^\/payment-billing$/, config: pagesConfig["/settings/payment-billing"] },
  { pattern: /^\/payment-methods$/, config: pagesConfig["/settings/payment-methods"] },
];

const PREPARE_HANDLERS = {
  "open-patients-advanced": () => {
    window.dispatchEvent(
      new CustomEvent("tour-page-action", {
        detail: { page: "patients", action: "open-advanced" },
      })
    );
  },
  "profile-tab-profile": () => {
    window.dispatchEvent(
      new CustomEvent("tour-tab-change", {
        detail: { page: "profile-settings", tab: "profile" },
      })
    );
  },
  "profile-tab-legal": () => {
    window.dispatchEvent(
      new CustomEvent("tour-tab-change", {
        detail: { page: "profile-settings", tab: "legal" },
      })
    );
  },
  "expand-patient-clinical-summary": () => {
    window.dispatchEvent(
      new CustomEvent("tour-page-action", {
        detail: { page: "patient-reports", action: "expand-clinical-summary" },
      })
    );
  },
  "video-tab-upcoming": () => {
    window.dispatchEvent(
      new CustomEvent("tour-tab-change", {
        detail: { page: "video-call", tab: "upcoming" },
      })
    );
  },
  "video-select-online-appointment": () => {
    window.dispatchEvent(
      new CustomEvent("tour-page-action", {
        detail: { page: "video-call", action: "select-online-appointment" },
      })
    );
  },
  "video-tab-join": () => {
    window.dispatchEvent(
      new CustomEvent("tour-tab-change", {
        detail: { page: "video-call", tab: "join" },
      })
    );
  },
  "video-tab-history": () => {
    window.dispatchEvent(
      new CustomEvent("tour-tab-change", {
        detail: { page: "video-call", tab: "history" },
      })
    );
  },
  "admin-tab-permissions": () => {
    window.dispatchEvent(
      new CustomEvent("tour-tab-change", {
        detail: { page: "admin-rbac", tab: "permissions" },
      })
    );
  },
  "admin-tab-roles": () => {
    window.dispatchEvent(
      new CustomEvent("tour-tab-change", {
        detail: { page: "admin-rbac", tab: "roles" },
      })
    );
  },
  "admin-tab-approvals": () => {
    window.dispatchEvent(
      new CustomEvent("tour-tab-change", {
        detail: { page: "admin-rbac", tab: "approvals" },
      })
    );
  },
  "admin-tab-invites": () => {
    window.dispatchEvent(
      new CustomEvent("tour-tab-change", {
        detail: { page: "admin-rbac", tab: "invites" },
      })
    );
  },
};

export const runFeaturePrepare = (prepareKey) => {
  const handler = PREPARE_HANDLERS[prepareKey];
  if (handler) handler();
};

const toFeatureId = (title = "feature", index = 0) =>
  `${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "feature"}-${index + 1}`;

const enrichPageConfig = (routePattern, config) => ({
  ...config,
  routePattern,
  features: (config.features || []).map((feature, index) => ({
    id: feature.id || toFeatureId(feature.title, index),
    ...feature,
  })),
});

export const PATIENT_DETAIL_PATH = /^\/patients\/[^/]+$/;

export const isPatientsTourRoute = (pathname = "/") => {
  const cleanPath = pathname.split("?")[0].split("#")[0] || "/";
  return cleanPath === "/patients" || PATIENT_DETAIL_PATH.test(cleanPath);
};

export const resolvePatientDetailPath = () => {
  const viewLink = document.querySelector("[data-tour='patients-view-record']");
  const href =
    viewLink?.getAttribute("href") ||
    viewLink?.closest("a")?.getAttribute("href") ||
    document
      .querySelector("[data-tour='patients-table'] a[href^='/patients/']")
      ?.getAttribute("href");

  if (href && href.startsWith("/patients/")) {
    return href.split("?")[0];
  }

  return null;
};

export const getPlatformStepIndexForPath = (pathname = "/", platformSteps = PLATFORM_TOUR_STEPS) => {
  const cleanPath = pathname.split("?")[0].split("#")[0] || "/";

  if (isPatientsTourRoute(cleanPath)) {
    return platformSteps.findIndex((step) => step.unifiedFeatures);
  }

  const exactIndex = platformSteps.findIndex(
    (step) => step.path === cleanPath && !step.match
  );
  if (exactIndex >= 0) return exactIndex;

  return -1;
};

export const resolvePlatformStepPath = (step) => {
  if (!step) return "/";
  return step.path;
};

const enrichPatientsPageConfig = (pathname, config) => {
  const cleanPath = pathname.split("?")[0].split("#")[0] || "/";
  const features = PATIENT_DETAIL_PATH.test(cleanPath)
    ? PATIENTS_DETAIL_FEATURES
    : cleanPath === "/patients"
      ? PATIENTS_LIST_FEATURES
      : config.features || [];

  return enrichPageConfig(pathname, { ...config, features });
};

export const getFeaturesForPlatformStep = (platformStepIndex, pathname, pageConfig, platformSteps = PLATFORM_TOUR_STEPS) => {
  const step = platformSteps[platformStepIndex];
  if (step?.unifiedFeatures) {
    return enrichPatientsPageConfig(pathname, pageConfig).features;
  }
  return pageConfig?.features || [];
};

export const getContextualTourPageConfig = (pathname = "/") => {
  const cleanPath = pathname.split("?")[0].split("#")[0] || "/";

  if (pagesConfig[cleanPath]) {
    const config = pagesConfig[cleanPath];
    if (cleanPath === "/patients") {
      return enrichPatientsPageConfig(cleanPath, config);
    }
    return enrichPageConfig(cleanPath, config);
  }

  const dynamicMatch = dynamicMatchers.find(({ pattern }) => pattern.test(cleanPath));
  if (dynamicMatch?.config) {
    if (PATIENT_DETAIL_PATH.test(cleanPath)) {
      return enrichPatientsPageConfig(cleanPath, dynamicMatch.config);
    }
    return enrichPageConfig(dynamicMatch.pattern.toString(), dynamicMatch.config);
  }

  return enrichPageConfig(cleanPath, pagesConfig.fallback);
};
